import React, { useState, useRef, useEffect } from 'react';
import { 
  FaInfoCircle, 
  FaQuestionCircle,
  FaExclamationTriangle,
  FaCog,
  FaLightbulb,
  FaShieldAlt,
  FaTimes
} from 'react-icons/fa';
import PropTypes from 'prop-types';
import './InfoPanel.css';

const TooltipInfoPanel = ({
  // Content Props
  title = "Information",
  items = [],
  children = null,
  
  // Icon Props
  icon = "info",
  iconSize = 16,
  iconColor = "#667eea",
  
  // Direction Props (New Enhanced)
  direction = "auto", // "top", "bottom", "left", "right", "auto"
  position = null, // Legacy support - will use direction if not specified
  maxWidth = "320px",
  
  // Styling Props
  variant = "default",
  className = "",
  
  // Behavior Props
  closeOnClickOutside = true,
  showCloseButton = true,
  
  // Event Props
  onToggle = null,
  
  // Advanced Props
  zIndex = 1000,
  offset = 8
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Handle legacy position prop and new direction prop
  const actualDirection = position || direction;
  const [calculatedDirection, setCalculatedDirection] = useState(actualDirection);
  const iconRef = useRef(null);
  const overlayRef = useRef(null);

  // Icon mapping
  const iconMap = {
    info: FaInfoCircle,
    question: FaQuestionCircle,
    warning: FaExclamationTriangle,
    settings: FaCog,
    tip: FaLightbulb,
    security: FaShieldAlt
  };

  // Get icon component
  const IconComponent = typeof icon === 'string' ? iconMap[icon] || FaInfoCircle : icon;

  // Calculate optimal direction when auto is selected
  const calculateDirection = () => {
    if (!iconRef.current || actualDirection !== 'auto') {
      setCalculatedDirection(actualDirection);
      return;
    }

    const iconRect = iconRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Check available space in each direction
    const spaceTop = iconRect.top;
    const spaceBottom = viewportHeight - iconRect.bottom;
    const spaceLeft = iconRect.left;
    const spaceRight = viewportWidth - iconRect.right;

    // Determine best direction based on available space
    // Priority: bottom > top > right > left (most natural reading flow)
    if (spaceBottom > 200) {
      setCalculatedDirection('bottom');
    } else if (spaceTop > 200) {
      setCalculatedDirection('top');
    } else if (spaceRight > 300) {
      setCalculatedDirection('right');
    } else if (spaceLeft > 300) {
      setCalculatedDirection('left');
    } else {
      // Fallback to direction with most space
      const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
      if (maxSpace === spaceBottom) setCalculatedDirection('bottom');
      else if (maxSpace === spaceTop) setCalculatedDirection('top');
      else if (maxSpace === spaceRight) setCalculatedDirection('right');
      else setCalculatedDirection('left');
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        closeOnClickOutside &&
        isOpen &&
        overlayRef.current &&
        iconRef.current &&
        !overlayRef.current.contains(event.target) &&
        !iconRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        if (onToggle) onToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnClickOutside, onToggle]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        if (onToggle) onToggle(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onToggle]);

  // Toggle overlay
  const toggleOverlay = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      calculateDirection();
    }
    if (onToggle) onToggle(newState);
  };

  // Close overlay
  const closeOverlay = () => {
    setIsOpen(false);
    if (onToggle) onToggle(false);
  };

  // Generate CSS classes
  const iconClasses = [
    'tooltip-info-icon',
    `variant-${variant}`,
    isOpen ? 'active' : '',
    className
  ].filter(Boolean).join(' ');

  const overlayClasses = [
    'tooltip-overlay',
    `direction-${calculatedDirection}`,
    `variant-${variant}`,
    isOpen ? 'visible' : 'hidden'
  ].join(' ');

  return (
    <div className="tooltip-info-container">
      {/* Info Icon Trigger */}
      <button
        ref={iconRef}
        className={iconClasses}
        onClick={toggleOverlay}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Show ${title}`}
        type="button"
        style={{
          '--icon-color': iconColor,
          '--z-index': zIndex
        }}
      >
        <IconComponent size={iconSize} />
      </button>

      {/* Overlay Content */}
      {isOpen && (
        <div
          ref={overlayRef}
          className={overlayClasses}
          role="dialog"
          aria-labelledby="tooltip-title"
          style={{
            '--max-width': maxWidth,
            '--z-index': zIndex,
            '--offset': `${offset}px`
          }}
        >
          {/* Overlay Header */}
          <div className="overlay-header">
            <h3 id="tooltip-title" className="overlay-title">
              <IconComponent size={14} className="title-icon" />
              {title}
            </h3>
            {showCloseButton && (
              <button
                className="close-button"
                onClick={closeOverlay}
                aria-label="Close"
                type="button"
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>

          {/* Overlay Content */}
          <div className="overlay-content">
            {children ? (
              children
            ) : items.length > 0 ? (
              <ul className="overlay-list">
                {items.map((item, index) => (
                  <li key={index} className="overlay-list-item">
                    {typeof item === 'string' ? item : item.content || item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="overlay-empty">No information available</p>
            )}
          </div>

          {/* Arrow/Pointer */}
          <div className="overlay-arrow" />
        </div>
      )}
    </div>
  );
};

// PropTypes
TooltipInfoPanel.propTypes = {
  // Content
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ])),
  children: PropTypes.node,
  
  // Icon
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.elementType
  ]),
  iconSize: PropTypes.number,
  iconColor: PropTypes.string,
  
  // Direction & Positioning
  direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'auto']),
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'auto']), // Legacy support
  maxWidth: PropTypes.string,
  
  // Styling
  variant: PropTypes.oneOf(['default', 'primary', 'warning', 'success', 'error']),
  className: PropTypes.string,
  
  // Behavior
  closeOnClickOutside: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  
  // Events
  onToggle: PropTypes.func,
  
  // Advanced
  zIndex: PropTypes.number,
  offset: PropTypes.number
};

export default TooltipInfoPanel;